import type { UniverseId } from '@/domain/universes/universe.interface';
import type { Branded } from '@/utils/typing/branded.type';
import type { BuildingId } from './Building.interface';
import type { ClassificationNodeId } from './Classification.interface';
import type { DeliveryPointId } from './DeliveryPoint.interface';
import type { UnitCode, UnitGroupCode } from './Unit.interface';

export enum TimeseriesIndicatorCode {
    COST_VAT_INCL = 'costVatIncl',
    COST_VAT_EXCL = 'costVatExcl',
    ENERGY_CONSUMPTION = 'energyConsumption',
    WATER_CONSUMPTION = 'waterConsumption',
    CO2 = 'co2',
    PRIMARY_CONSUMPTION = 'primaryConsumption'
}

export enum TimeseriesStep {
    DAY = 'day',
    MONTH = 'month',
    YEAR = 'year'
}

export interface DeliveryPointTimeseries {
    deliveryPointId: DeliveryPointId;
    metric: DeliveryPointMetric;
    indicatorCode: TimeseriesIndicatorCode;
    unitCode: UnitCode;
    values: TimeseriesPoint[];
}

export type ValuesBySources = {
    [Metric in DeliveryPointMetric]: number;
};

export type DeliveryPointIndicator = {
    deliveryPointId: DeliveryPointId;
    date: string;
    indicatorCode: TimeseriesIndicatorCode;
} & {
    [Metric in Exclude<DeliveryPointMetric, 'budget_or_predicted'>]: number | null;
} & {
    [Metric in DeliveryPointMetricCompleteness]: number | null;
};
// tslint:enable:interface-over-type-literal

export type DeliveryPointMetric = DeliveryPointBasicMetric | DeliveryPointCompositeMetric;
export type DeliveryPointCompositeMetric = 'measured' | 'estimated' | 'predicted' | 'budget' | 'budget_or_predicted';
export type DeliveryPointBasicMetric =
    | 'invoice'
    | 'distributorBilling'
    | 'distributorReading'
    | 'manualReading'
    | 'iotReading'
    | 'gtbReading'
    | 'ipmvpPrediction'
    | 'providerBilling'
    | 'distributorYearlyConsumption'
    | 'clientReading';

// Use new typescript 4 string literal feature to generate this one day when updting
export type DeliveryPointMetricCompleteness = DeliveryPointBasicMetricCompleteness | DeliveryPointCompositeMetricCompleteness;
export type DeliveryPointCompositeMetricCompleteness =
    | 'measuredCompleteness'
    | 'estimatedCompleteness'
    | 'predictedCompleteness'
    | 'budgetCompleteness';
export type DeliveryPointBasicMetricCompleteness =
    | 'invoiceCompleteness'
    | 'distributorBillingCompleteness'
    | 'distributorReadingCompleteness'
    | 'manualReadingCompleteness'
    | 'iotReadingCompleteness'
    | 'gtbReadingCompleteness'
    | 'ipmvpPredictionCompleteness'
    | 'providerBillingCompleteness'
    | 'distributorYearlyConsumptionCompleteness'
    | 'clientReadingCompleteness';

export interface TimeseriesPoint {
    timestamp: number;
    value: number;
    details?: TimeseriesDetails[];
}

export interface TimeseriesDetails {
    source: string;
    year: number;
    value: number | null;
}
export interface TimeseriesOriginalMetric extends DeliveryPointTimeseries {
    values: TimeseriesPointOriginalMetric[];
}

export interface TimeseriesPointOriginalMetric extends TimeseriesPoint {
    originalMetric?: DeliveryPointMetric;
}

export interface TimeseriesIndicatorAndUnitCode {
    indicatorCode: TimeseriesIndicatorCode;
    unitCode: UnitCode;
}

export interface DeliveryPointTimeseriesRequestFilter {
    buildingIds?: BuildingId[];
    universeIds?: UniverseId[];
    deliveryPointIds?: DeliveryPointId[];
}

export interface SenTimeseriesParams {
    timeseriesId: SensorTimeseriesId;
    unitGroupCode: UnitGroupCode | null;
}

export type SensorTimeseriesId = Branded<number, 'SensorTimeseriesId'>;

export function sensorTimeseriesId(input: number): SensorTimeseriesId {
    return input as SensorTimeseriesId;
}

export interface TimeseriesIndicatorDailyMonthlyResponse {
    deliveryPointId: DeliveryPointId;
    indicatorCode: TimeseriesIndicatorCode;
    unitCode: UnitCode;
    date: string;
    dayCount: ValuesBySources;
    expectedDayCount: ValuesBySources;
    values: ValuesBySources;
}

export interface DeliveryPointTimeseriesIndicatorYearlyRequest {
    deliveryPointIds: DeliveryPointId[];
    indicatorCodes: TimeseriesIndicatorCode[];
    startDate?: string;
    endDate?: string;
    years?: number[];
    withCompleteness: boolean;
}

// tslint:disable:interface-over-type-literal

export type UniverseAggregatedDeliveryPointTimeseriesPoint = {
    date: string;
    universeId: UniverseId;
    indicatorCode: TimeseriesIndicatorCode;
    metric: DeliveryPointMetric;
    value: number | null;
    details: TimeseriesDetails[];
};
// tslint:enable:interface-over-type-literal

export interface UniverseAggregatedDeliveryPointTimeseriesFilters {
    deliveryPointIds?: DeliveryPointId[];
    universeIds?: UniverseId[];
    classificationNodeId?: ClassificationNodeId;
    indicatorCodes: TimeseriesIndicatorCode[];
    metrics?: DeliveryPointMetric[];
}
